import {useState, useCallback, useMemo} from 'react';
import {Step} from '../../components/common/arr-stepper/ArrStepper';

interface UseMultiStepFormProps {
  stepDefinitions: Array<{
    key: string;
    label: string;
    tooltipTitle?: string;
    tooltip?: string;
  }>;
  initialStepKey?: string;
  onStepChange?: (stepKey: string) => void;
  onComplete?: (data: any) => void;
}

export const useMultiStepForm = ({
  stepDefinitions,
  initialStepKey,
  onStepChange,
  onComplete,
}: UseMultiStepFormProps) => {
  const [currentStepKey, setCurrentStepKey] = useState(
    initialStepKey || stepDefinitions[0]?.key
  );
  const [stepData, setStepData] = useState<any>({});

  // Generate steps with proper state
  const steps: Step[] = useMemo(() => {
    const currentIndex = stepDefinitions.findIndex(
      (step) => step.key === currentStepKey
    );

    return stepDefinitions.map((step, index) => ({
      key: step.key,
      label: step.label,
      tooltipTitle: step.tooltipTitle,
      tooltip: step.tooltip,
      state:
        index < currentIndex
          ? 'completed'
          : index === currentIndex
            ? 'current'
            : 'future',
    }));
  }, [stepDefinitions, currentStepKey]);

  const goNext = useCallback(() => {
    const currentIndex = stepDefinitions.findIndex(
      (step) => step.key === currentStepKey
    );
    if (currentIndex < stepDefinitions.length - 1) {
      const nextStep = stepDefinitions[currentIndex + 1];
      setCurrentStepKey(nextStep.key);
      onStepChange?.(nextStep.key);
    } else {
      onComplete?.(stepData);
    }
  }, [currentStepKey, stepDefinitions, stepData, onStepChange, onComplete]);

  const goBack = useCallback(() => {
    const currentIndex = stepDefinitions.findIndex(
      (step) => step.key === currentStepKey
    );
    if (currentIndex > 0) {
      const prevStep = stepDefinitions[currentIndex - 1];
      setCurrentStepKey(prevStep.key);
      onStepChange?.(prevStep.key);
    }
  }, [currentStepKey, stepDefinitions, onStepChange]);

  const goToStep = useCallback(
    (stepKey: string) => {
      setCurrentStepKey(stepKey);
      onStepChange?.(stepKey);
    },
    [onStepChange]
  );

  const updateStepData = useCallback((data: any) => {
    setStepData((prev: any) => ({...prev, ...data}));
  }, []);

  const currentStepIndex = stepDefinitions.findIndex(
    (step) => step.key === currentStepKey
  );
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === stepDefinitions.length - 1;

  return {
    currentStepKey,
    steps,
    stepData,
    goNext,
    goBack,
    goToStep,
    updateStepData,
    isFirstStep,
    isLastStep,
    currentStepIndex,
  };
};
